import type { PublicSignerFieldResponse } from '../publicSignerTypes';

import { isFieldEditable } from './fieldValue';
import type { PublicSignerFieldErrorMap } from './types';

export function getOrderedEditableFields(fields: PublicSignerFieldResponse[]) {
  const getFieldMidline = (field: PublicSignerFieldResponse) =>
    field.y + field.height / 2;
  const getSharedRowTolerance = (
    left: PublicSignerFieldResponse,
    right: PublicSignerFieldResponse
  ) => Math.max(Math.min(left.height, right.height) * 0.6, 0.012);

  return [...fields].filter(isFieldEditable).sort((left, right) => {
    const pageDifference = left.page_index - right.page_index;

    if (pageDifference !== 0) {
      return pageDifference;
    }

    const midlineDifference = getFieldMidline(left) - getFieldMidline(right);

    if (Math.abs(midlineDifference) <= getSharedRowTolerance(left, right)) {
      return left.x - right.x || left.y - right.y;
    }

    return midlineDifference || left.x - right.x;
  });
}

export function findNextFieldId(
  orderedFields: PublicSignerFieldResponse[],
  selectedFieldId: string | null
) {
  if (orderedFields.length === 0) {
    return null;
  }

  const currentIndex = orderedFields.findIndex(
    (field) => field.id === selectedFieldId
  );

  if (currentIndex < 0) {
    return orderedFields[0]?.id ?? null;
  }

  return orderedFields[(currentIndex + 1) % orderedFields.length]?.id ?? null;
}

export function findPreviousFieldId(
  orderedFields: PublicSignerFieldResponse[],
  selectedFieldId: string | null
) {
  if (orderedFields.length === 0) {
    return null;
  }

  const currentIndex = orderedFields.findIndex(
    (field) => field.id === selectedFieldId
  );

  if (currentIndex < 0) {
    return orderedFields.at(-1)?.id ?? null;
  }

  const previousIndex =
    (currentIndex - 1 + orderedFields.length) % orderedFields.length;
  return orderedFields[previousIndex]?.id ?? null;
}

export function findFirstInvalidFieldId(
  fields: PublicSignerFieldResponse[],
  fieldErrors: PublicSignerFieldErrorMap
) {
  const invalidFieldIds = new Set(Object.keys(fieldErrors));

  return (
    getOrderedEditableFields(fields).find((field) =>
      invalidFieldIds.has(field.id)
    )?.id ??
    Object.keys(fieldErrors)[0] ??
    null
  );
}

export function findNextIncompleteRequiredFieldId(
  orderedFields: PublicSignerFieldResponse[],
  selectedFieldId: string | null,
  isIncompleteRequired: (field: PublicSignerFieldResponse) => boolean
) {
  if (orderedFields.length === 0) {
    return null;
  }

  const candidates = orderedFields.filter(isIncompleteRequired);
  if (candidates.length === 0) {
    return null;
  }

  const currentIndex = orderedFields.findIndex(
    (field) => field.id === selectedFieldId
  );

  const nextAfterCurrent = candidates.find(
    (field) => orderedFields.indexOf(field) > currentIndex
  );

  return (nextAfterCurrent ?? candidates[0])?.id ?? null;
}
